export const userDisplayQuery = {
    select: {
        id: true,
        email: true,
        firstName: true,
        lastName: true,
        avatarUrl: true,
    },
};
/**
 * Catalog of pages a government user may pick as their landing page in the internal app. The
 * `value` is the stable identifier persisted on `User.landingPage` and validated by the `/me`
 * endpoint; the `path` is where the app root (`/`) sends the user. When a route moves, update its
 * `path` here rather than introducing a new `value`, so existing preferences keep working.
 *
 * Values are namespaced `<section>:<page>` after the app's top-level navigation, so a page can be
 * added under a section without inventing a globally unique name, and consumers can gate a whole
 * section (a module the government doesn't have) by its prefix. Sections with a single page carry
 * no prefix.
 *
 * Which of these a given user is actually offered depends on their government's modules and their
 * permissions — that gating lives in the web app, next to the route gates it mirrors.
 */
export const landingPages = [
    { value: 'meetings:upcoming', label: 'Meetings: Live & Upcoming', path: '/meetings' },
    { value: 'meetings:past', label: 'Meetings: Past Meetings', path: '/meetings/past' },
    { value: 'meetings:calendar', label: 'Meetings: Calendar', path: '/meetings/calendar' },
    { value: 'legislative_items:all', label: 'Legislation: All Items', path: '/legislative-items' },
    {
        value: 'legislative_items:assignments',
        label: 'Legislation: Item Assignments',
        path: '/legislative-items/my-assignments',
    },
    {
        value: 'legislative_items:followed',
        label: 'Legislation: Followed Items',
        path: '/legislative-items/following',
    },
    { value: 'boards:roster', label: 'Boards: Board Roster', path: '/board-roster' },
    { value: 'boards:health', label: 'Boards: Board Health', path: '/board-management' },
    {
        value: 'boards:applicant_tracking',
        label: 'Boards: Applicant Tracking',
        path: '/applicant-tracking',
    },
    {
        value: 'boards:application_reviews',
        label: 'Boards: My Application Reviews',
        path: '/applicant-reviews',
    },
    {
        value: 'boards:applicant_directory',
        label: 'Boards: Applicant Directory',
        path: '/applicant-directory',
    },
    { value: 'reports', label: 'Reports', path: '/reports' },
];
/**
 * Whether a stored value still matches one of the landing page options. Deliberately returns a
 * plain boolean rather than narrowing to {@link LandingPageValue}: callers keep the free-form
 * string type of the column, so a value stored before an option was retired still reads back.
 */
export const isLandingPageValue = (value) => landingPages.some((landingPage) => landingPage.value === value);
/** Where the app root sends users who have no landing page preference, or an unreachable one. */
export const DEFAULT_LANDING_PAGE_PATH = '/meetings';
